/**
 * React Query hooks for pricing data fetching
 */

import { useQuery } from '@tanstack/react-query';
import { PricingService } from '../../../utils/services/pricingService';
import { API_URL } from '../../../config';

/**
 * Query keys for pricing
 */
export const pricingQueryKeys = {
  all: ['pricing'] as const,
  config: () => ['pricing', 'config'] as const,
};

/**
 * Fetch pricing configuration with automatic caching
 *
 * Features:
 * - No authentication required (public endpoint)
 * - Cached for 10 minutes (pricing changes infrequently)
 * - Graceful fallback to default pricing on error
 *
 * @example
 * ```typescript
 * const { data: pricing } = usePricing();
 *
 * return <div>{pricing.currencySymbol}{pricing.articleBasePrice}</div>;
 * ```
 */
export function usePricing() {
  return useQuery({
    queryKey: pricingQueryKeys.config(),
    queryFn: async () => {
      const service = new PricingService(API_URL);
      const response = await service.getPricing();

      // Handle ErrorHandlingResponse type from service
      if ('success' in response && !response.success) {
        console.warn('Pricing fetch failed, using fallback:', response.error);
        // Fallback is already in response.data
        return response.data!;
      }

      return response.data!;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - pricing rarely changes
    gcTime: 30 * 60 * 1000, // 30 minutes cache
    refetchOnWindowFocus: false, // Don't refetch pricing on focus
    retry: 1, // Retry once on failure
  });
}
